// ============================================
// Memory Edit Module - Index (ES6)
// 메모리 편집 모듈 진입점
// XSS 방지: 모든 레이어 렌더러에서 escapeHtml 사용
// ============================================

import { escapeHtml } from '../utils.js';
import {
  setCurrentMemorySession,
  getCurrentMemorySessionId,
  getLayerData,
  resetLayerData,
  isAccordionOpen,
  isLayerLoading
} from './state.js';
import { renderAccordion, LAYER_CONFIG, LAYER_ORDER } from './accordion.js';
import { setupMemoryEditEvents } from './events.js';
import { loadLayerDataFromFirestore, loadAllLayerData } from './api.js';

// 레이어 렌더러 import (모두 escapeHtml 적용됨)
import { renderEventLayer } from './layers/event.js';
import { renderQuoteLayer } from './layers/quote.js';
import { renderSummaryLayer } from './layers/summary.js';

// ============================================
// 메모리 편집 탭 렌더링
// ============================================

export async function renderMemoryEditTab(userId, roomId, sessionId) {
  const content = document.getElementById('memoryContent');
  if (!content) return;

  // 캐시 확인: 같은 세션이고 데이터가 있으면 UI만 렌더링
  const cachedSessionId = getCurrentMemorySessionId();
  const eventData = getLayerData('event'); // 기본 열린 레이어의 데이터 확인

  if (cachedSessionId === sessionId && eventData !== null) {
    // 데이터 캐시가 있으면 바로 UI 렌더링
    renderMemoryEditUI(content);
    return;
  }

  // 새 세션: 로딩 표시 후 데이터 로드
  content.innerHTML = `
    <div class="memory-edit-loading">
      <div class="spinner"></div>
      <span>메모리 로드 중...</span>
    </div>
  `;

  // 세션 정보 설정 및 데이터 초기화
  setCurrentMemorySession(userId, roomId, sessionId);
  resetLayerData();

  // 열려있는 아코디언의 데이터만 먼저 로드
  const openLayers = LAYER_ORDER.filter(layer => isAccordionOpen(layer));
  await Promise.all(openLayers.map(layer => loadLayerDataFromFirestore(layer)));

  // UI 렌더링
  renderMemoryEditUI(content);
}

// ============================================
// UI 렌더링
// ============================================

function renderMemoryEditUI(content) {
  // renderAccordion과 renderLayerContent는 escapeHtml로 안전하게 처리됨
  content.innerHTML = renderAccordion(renderLayerContent);

  // 열린 아코디언 초기화 (클래스 + max-height)
  initializeOpenAccordions(content);

  // 기존 이벤트 리스너 제거를 위해 DOM 교체
  const freshContent = content.cloneNode(true);
  content.parentNode.replaceChild(freshContent, content);

  // 이벤트 설정 (새 DOM에)
  setupMemoryEditEvents(freshContent, () => {
    // 데이터 변경 시 UI 업데이트
    updateAccordionUI(freshContent);
  });
}

// ============================================
// 열린 아코디언 초기화
// ============================================

function initializeOpenAccordions(container) {
  LAYER_ORDER.forEach(layer => {
    if (isAccordionOpen(layer)) {
      const accordionItem = container.querySelector(`.memory-accordion-item[data-layer="${layer}"]`);
      if (accordionItem) {
        // open 클래스 추가 (탭 복귀 시 필요)
        accordionItem.classList.add('open');
        const contentEl = accordionItem.querySelector('.memory-accordion-content');
        if (contentEl) {
          contentEl.style.maxHeight = 'none';
        }
      }
    }
  });
}

// ============================================
// 레이어 콘텐츠 렌더링 (각 렌더러에서 escapeHtml 사용)
// ============================================

function renderLayerContent(layer, data) {
  switch (layer) {
    case 'event':
      return renderEventLayer(data);
    case 'quote':
      return renderQuoteLayer(data);
    case 'summary':
      return renderSummaryLayer(data);
    default:
      return '<div class="memory-layer-empty">알 수 없는 레이어</div>';
  }
}

// ============================================
// 아코디언 UI 업데이트
// ============================================

function updateAccordionUI(container) {
  LAYER_ORDER.forEach(layer => {
    const accordionItem = container.querySelector(`.memory-accordion-item[data-layer="${layer}"]`);
    if (!accordionItem) return;

    const contentEl = accordionItem.querySelector('.memory-accordion-content');
    const countEl = accordionItem.querySelector('.accordion-count');
    const data = getLayerData(layer);
    const loading = isLayerLoading(layer);

    // 콘텐츠 업데이트 (레이어 렌더러에서 escapeHtml 처리)
    if (contentEl && isAccordionOpen(layer)) {
      if (loading) {
        contentEl.innerHTML = '<div class="memory-layer-loading"><div class="spinner"></div></div>';
      } else {
        contentEl.innerHTML = renderLayerContent(layer, data);
      }
    }

    // 카운트 업데이트 (숫자만 표시, XSS 위험 없음)
    if (countEl) {
      const count = getLayerCount(layer, data);
      countEl.textContent = String(count);
      countEl.style.display = count > 0 ? '' : 'none';
    }
  });
}

// ============================================
// 레이어 카운트 계산
// ============================================

function getLayerCount(layer, data) {
  if (!data) return 0;

  switch (layer) {
    case 'event':
    case 'quote':
      return Array.isArray(data) ? data.length : 0;
    default:
      return 0;
  }
}

// ============================================
// Export
// ============================================

export { LAYER_CONFIG, LAYER_ORDER } from './accordion.js';
export { openEditSheet, closeEditSheet } from './edit-sheet.js';
export { loadAllLayerData } from './api.js';
